import { ACCENTS, GREYS, PALETTE } from "../../../utils/core";
import "./style.css";
import MyPopup from "../MyPopup";
import Icon from "../Icon";
/**
 * Props:
 * - value: { background: "#ffffff", text: "#000000" }
 * - onChange: (newValue) => void
 */

interface SwatchProps {
  color: string;
  selected: boolean;
  onClick: (color: string) => void;
  label?: string;
}

function Swatch({ color, selected, onClick, label }: SwatchProps) {
  return (
    <button
      className="swatch"
      title={label || color}
      aria-pressed={selected}
      onClick={() => onClick(color)}
      style={{ background: color }}
    >
      {selected && (
        <svg className="check" viewBox="0 0 24 24" aria-hidden>
          <path
            d="M20 6L9 17l-5-5"
            stroke="white"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      )}
    </button>
  );
}

interface ColorValue {
  background: string | null;
  text: string | null;
}

interface ColorPickerProps {
  value?: ColorValue;
  onChange?: (value: ColorValue) => void;
}

export default function ColorPicker({
  value = { background: null, text: null },
  onChange = () => {},
}: ColorPickerProps) {
  function update(which: keyof ColorValue, color: string | null) {
    onChange({ ...value, [which]: color });
  }

  return (
    <div>
      <MyPopup
        trigger={
          <button
            className="font-option heading tool-bar-button"
            data-tooltip="Text color"
          >
            <Icon name="Styling" className="tool-bar-button-icon" />
            <Icon name="DownArrow" className="tool-bar-button-icon" />
          </button>
        }
      >
        {({ close }) => (
          <div
            className="color-popover mail-inbox-popup-content"
            role="dialog"
            aria-label="Choose colors"
          >
            <div className="color-columns">
              <section className="color-column" aria-labelledby="bg-label">
                <h4 id="bg-label">Background color</h4>

                <div className="row">
                  <button
                    className="swatch clear-swatch"
                    title="Remove color"
                    aria-pressed={!value.background}
                    onClick={() => {
                      update("background", null);
                      close();
                    }}
                    style={{ background: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Icon name="Ban" size={14} color="#666" />
                  </button>
                </div>

                <div className="row">
                  {GREYS.map((c) => (
                    <Swatch
                      key={c + "bg"}
                      color={c}
                      selected={value.background === c}
                      onClick={(col) => {
                        update("background", col);
                        close();
                      }}
                      label={c}
                    />
                  ))}
                </div>

                <div className="row accent-row">
                  {ACCENTS.map((c) => (
                    <Swatch
                      key={c + "bgacc"}
                      color={c}
                      selected={value.background === c}
                      onClick={(col) => {
                        update("background", col);
                        close();
                      }}
                      label={c}
                    />
                  ))}
                </div>

                <div className="palette">
                  {PALETTE.map((r, i) => (
                    <div className="palette-row" key={"row" + i}>
                      {r.map((c) => (
                        <Swatch
                          key={c + "bgp" + i}
                          color={c}
                          selected={value.background === c}
                          onClick={(col) => {
                            update("background", col);
                            close();
                          }}
                          label={c}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </section>

              <section className="color-column" aria-labelledby="text-label">
                <h4 id="text-label">Text color</h4>

                <div className="row">
                  <button
                    className="swatch clear-swatch"
                    title="Remove color"
                    aria-pressed={!value.text}
                    onClick={() => {
                      update("text", null);
                      close();
                    }}
                    style={{ background: "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <Icon name="Ban" size={14} color="#666" />
                  </button>
                </div>

                <div className="row">
                  {GREYS.map((c) => (
                    <Swatch
                      key={c + "tx"}
                      color={c}
                      selected={value.text === c}
                      onClick={(col) => {
                        update("text", col);
                        close();
                      }}
                      label={c}
                    />
                  ))}
                </div>

                <div className="row accent-row">
                  {ACCENTS.map((c) => (
                    <Swatch
                      key={c + "txacc"}
                      color={c}
                      selected={value.text === c}
                      onClick={(col) => {
                        update("text", col);
                        close();
                      }}
                      label={c}
                    />
                  ))}
                </div>

                <div className="palette">
                  {PALETTE.map((r, i) => (
                    <div className="palette-row" key={"txrow" + i}>
                      {r.map((c) => (
                        <Swatch
                          key={c + "txp" + i}
                          color={c}
                          selected={value.text === c}
                          onClick={(col) => {
                            update("text", col);
                            close();
                          }}
                          label={c}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        )}
      </MyPopup>
    </div>
  );
}
